import React, { useState, useEffect } from 'react';
import { Sparkles, Power, Play, Square, AlertCircle, CheckCircle, Loader2, Edit2, Save, X, AlertTriangle } from 'lucide-react';

export default function AITestingPage() {
  const [handlerRunning, setHandlerRunning] = useState(false);
  const [testConversationId, setTestConversationId] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [apiKey, setApiKey] = useState('nTRMyuRXuqbCnchywVZIJ85txv8DOpha');
  const [anthropicKey, setAnthropicKey] = useState('sk-ant-api03-your-key-here'); // Replace with actual key
  const [testCustomerMessage, setTestCustomerMessage] = useState('');
  const [conversationMessages, setConversationMessages] = useState<Array<{role: string, content: string}>>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [emergencyStop, setEmergencyStop] = useState(false);

  // Load conversation ID from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const conversationParam = params.get('conversation');
    if (conversationParam) {
      setTestConversationId(conversationParam);
    }
  }, []);

  useEffect(() => {
    checkHandlerStatus();
    const interval = setInterval(checkHandlerStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const checkHandlerStatus = async () => {
    try {
      const response = await fetch('/api/ai/status?api_key=synapse-dev');
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      setHandlerRunning(data.running);
      if (data.logs) {
        setLogs(data.logs.slice(-10));
      }
    } catch (error) {
      console.log('AI API not available yet');
    }
  };

  const startHandler = async () => {
    if (!apiKey || !anthropicKey) {
      alert('Please set API keys first');
      return;
    }

    try {
      const response = await fetch('/api/ai/start?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          japlanchers_api_key: apiKey,
          anthropic_api_key: anthropicKey
        })
      });

      const data = await response.json();
      if (data.success) {
        addLog('✅ AI Handler started');
        setHandlerRunning(true);
      } else {
        addLog(`❌ ${data.error || 'Failed to start handler'}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${error}`);
    }
  };

  const stopHandler = async () => {
    try {
      const response = await fetch('/api/ai/stop?api_key=synapse-dev', { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        addLog('⏹️ AI Handler stopped');
        setHandlerRunning(false);
      } else {
        addLog(`❌ ${data.error || 'Failed to stop handler'}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${error}`);
    }
  };

  const toggleEmergencyStop = async () => {
    try {
      const newState = !emergencyStop;
      const response = await fetch('/api/ai/emergency-stop?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newState })
      });

      const data = await response.json();
      if (data.success) {
        setEmergencyStop(newState);
        addLog(newState ? '🚨 EMERGENCY STOP ACTIVATED - AI responses disabled' : '✅ Emergency stop deactivated - AI responses enabled');
      } else {
        addLog(`❌ ${data.error || 'Failed to toggle emergency stop'}`);
      }
    } catch (error) {
      addLog(`❌ Error: ${error}`);
    }
  };

  const testGenerate = async () => {
    if (!testConversationId) {
      alert('Please enter a conversation ID');
      return;
    }

    setTesting(true);
    setTestResponse('');
    addLog(`🧪 Testing AI response for conversation ${testConversationId}...`);

    try {
      const response = await fetch('/api/ai/generate?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: testConversationId,
          anthropic_api_key: anthropicKey
        })
      });

      const data = await response.json();
      if (data.response) {
        setTestResponse(data.response);
        addLog(`✅ Response generated for ${data.client_name || 'client'} (${data.message_count || 0} messages)`);
      } else if (data.error) {
        addLog(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Failed to generate: ${error}`);
    } finally {
      setTesting(false);
    }
  };

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`].slice(-20));
  };

  const createTestConversation = async () => {
    try {
      addLog('🧪 Creating test conversation...');

      // Create fake customer
      const response = await fetch('/api/ai/create-test-conversation?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: 'Test Customer ' + Math.floor(Math.random() * 1000),
          initial_message: testCustomerMessage || 'Hi, I need help with my hardwood floors.'
        })
      });

      const data = await response.json();
      if (data.success) {
        setTestConversationId(data.conversation_id);
        setConversationMessages([{ role: 'customer', content: data.initial_message }]);
        addLog(`✅ Created test conversation: ${data.conversation_id.substring(0, 8)}...`);
      } else {
        addLog(`❌ ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Failed to create: ${error}`);
    }
  };

  const sendCustomerMessage = async () => {
    if (!testConversationId || !testCustomerMessage) {
      alert('Need conversation ID and message');
      return;
    }

    try {
      const response = await fetch('/api/ai/send-message?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: testConversationId,
          message: testCustomerMessage,
          sender_type: 'client'
        })
      });

      const data = await response.json();
      if (data.success) {
        setConversationMessages(prev => [...prev, { role: 'customer', content: testCustomerMessage }]);
        setTestCustomerMessage('');
        addLog('✅ Customer message sent');
      } else {
        addLog(`❌ ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Failed: ${error}`);
    }
  };

  const generateAndShow = async () => {
    if (!testConversationId) {
      alert('Create or load a conversation first');
      return;
    }

    setTesting(true);
    addLog(`🤖 Generating AI response...`);

    try {
      const response = await fetch('/api/ai/generate?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: testConversationId,
          anthropic_api_key: anthropicKey
        })
      });

      const data = await response.json();
      if (data.response) {
        setConversationMessages(prev => [...prev, { role: 'ai', content: data.response }]);
        addLog(`✅ AI responded`);
      } else if (data.error) {
        addLog(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Failed: ${error}`);
    } finally {
      setTesting(false);
    }
  };

  const startEditing = (index: number, content: string) => {
    setEditingIndex(index);
    setEditedContent(content);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditedContent('');
  };

  const saveCorrection = async (index: number, originalContent: string) => {
    if (!editedContent.trim()) {
      alert('Corrected response cannot be empty');
      return;
    }

    try {
      addLog('💾 Saving response correction to knowledge base...');

      // Get the customer message that prompted this response
      const customerMsg = conversationMessages[index - 1]?.content || 'Unknown question';

      const response = await fetch('/api/ai/save-correction?api_key=synapse-dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: customerMsg,
          incorrect_response: originalContent,
          correct_response: editedContent
        })
      });

      const data = await response.json();
      if (data.success) {
        // Update the message in the conversation
        setConversationMessages(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], content: editedContent };
          return updated;
        });
        setEditingIndex(null);
        setEditedContent('');
        addLog('✅ Correction saved to knowledge base');
      } else {
        addLog(`❌ Failed to save: ${data.error}`);
      }
    } catch (error) {
      addLog(`❌ Error saving correction: ${error}`);
    }
  };

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            AI Testing Bench
          </h1>
          <p className="text-gray-400">Test and configure the AI conversation handler</p>
        </div>

        {/* Handler Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d] rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Power className="w-5 h-5" />
              Handler Status
            </h2>

            <div className="flex items-center gap-3 mb-4">
              <div className={`w-3 h-3 rounded-full ${handlerRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
              <span className={`font-medium ${handlerRunning ? 'text-green-400' : 'text-gray-400'}`}>
                {handlerRunning ? 'Running' : 'Stopped'}
              </span>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={startHandler}
                disabled={handlerRunning}
                className="flex-1 px-4 py-2 bg-green-500/20 hover:bg-green-500/30
                         disabled:bg-gray-500/20 disabled:cursor-not-allowed
                         text-green-400 disabled:text-gray-500 rounded-lg
                         flex items-center justify-center gap-2 transition"
              >
                <Play className="w-4 h-4" />
                Start
              </button>
              <button
                onClick={stopHandler}
                disabled={!handlerRunning}
                className="flex-1 px-4 py-2 bg-red-500/20 hover:bg-red-500/30
                         disabled:bg-gray-500/20 disabled:cursor-not-allowed
                         text-red-400 disabled:text-gray-500 rounded-lg
                         flex items-center justify-center gap-2 transition"
              >
                <Square className="w-4 h-4" />
                Stop
              </button>
            </div>

            {/* Emergency Stop */}
            <button
              onClick={toggleEmergencyStop}
              className={`w-full px-4 py-3 rounded-lg font-medium transition-all
                       flex items-center justify-center gap-2 ${
                emergencyStop
                  ? 'bg-red-500/30 border-2 border-red-500 text-red-400 animate-pulse'
                  : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20'
              }`}
            >
              <AlertTriangle className="w-5 h-5" />
              {emergencyStop ? 'EMERGENCY STOP ACTIVE' : 'Emergency Stop (OFF)'}
            </button>
            {emergencyStop && (
              <p className="text-xs text-red-400 mt-2 text-center">
                AI will not respond to any messages
              </p>
            )}
          </div>

          {/* API Keys */}
          <div className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d] rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">API Keys</h2>

            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">J&APlanchers API Key</label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter API key..."
                  className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                           text-gray-300 placeholder-gray-500 text-sm
                           focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Anthropic API Key</label>
                <input
                  type="password"
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                  placeholder="Enter API key..."
                  className="w-full px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                           text-gray-300 placeholder-gray-500 text-sm
                           focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Test Response Generation */}
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d] rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Test Response Generation</h2>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={testConversationId}
              onChange={(e) => setTestConversationId(e.target.value)}
              placeholder="Enter conversation ID..."
              className="flex-1 px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                       text-gray-300 placeholder-gray-500 text-sm
                       focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
            />
            <button
              onClick={testGenerate}
              disabled={testing}
              className="px-5 py-2 bg-gradient-to-r from-purple-600 to-pink-600
                       hover:from-purple-500 hover:to-pink-500
                       disabled:from-gray-600 disabled:to-gray-700
                       text-white font-medium rounded-lg
                       flex items-center gap-2 transition-all"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate
                </>
              )}
            </button>
          </div>

          {testResponse && (
            <div className="bg-[#0d1117] border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-green-400">Generated Response</span>
              </div>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{testResponse}</p>
            </div>
          )}
        </div>

        {/* Conversation Simulator */}
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d] rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Conversation Simulator</h2>

          {/* Controls */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={testCustomerMessage}
              onChange={(e) => setTestCustomerMessage(e.target.value)}
              placeholder="Type customer message..."
              className="flex-1 px-3 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg
                       text-gray-300 placeholder-gray-500 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
            />
            <button
              onClick={createTestConversation}
              className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30
                       text-blue-400 font-medium rounded-lg flex items-center gap-2"
            >
              Create Test
            </button>
            {testConversationId && (
              <>
                <button
                  onClick={sendCustomerMessage}
                  disabled={!testCustomerMessage}
                  className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30
                           disabled:bg-gray-500/20 text-green-400 disabled:text-gray-500
                           font-medium rounded-lg"
                >
                  Send
                </button>
                <button
                  onClick={generateAndShow}
                  disabled={testing}
                  className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30
                           disabled:bg-gray-500/20 text-purple-400 disabled:text-gray-500
                           font-medium rounded-lg flex items-center gap-2"
                >
                  {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  AI Reply
                </button>
              </>
            )}
          </div>

          {/* Conversation Thread */}
          {conversationMessages.length > 0 && (
            <div className="bg-[#0d1117] border border-[#30363d] rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
              {conversationMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'customer' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] ${
                    msg.role === 'customer'
                      ? 'bg-blue-500/20 text-blue-100 border border-blue-500/30'
                      : 'bg-purple-500/20 text-purple-100 border border-purple-500/30'
                  } rounded-lg overflow-hidden`}>
                    <div className="px-4 py-2">
                      <div className="text-xs opacity-60 mb-1 flex items-center justify-between">
                        <span>{msg.role === 'customer' ? 'Customer' : 'AI Assistant'}</span>
                        {msg.role === 'ai' && editingIndex !== i && (
                          <button
                            onClick={() => startEditing(i, msg.content)}
                            className="ml-2 p-1 hover:bg-white/10 rounded transition"
                            title="Edit response"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {editingIndex === i ? (
                        <div className="space-y-2">
                          <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="w-full px-2 py-1 bg-[#0d1117] border border-purple-500/50 rounded
                                     text-sm text-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500
                                     min-h-[80px] resize-y"
                            autoFocus
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={cancelEditing}
                              className="px-3 py-1 text-xs bg-gray-500/20 hover:bg-gray-500/30
                                       text-gray-400 rounded flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                            <button
                              onClick={() => saveCorrection(i, msg.content)}
                              className="px-3 py-1 text-xs bg-green-500/20 hover:bg-green-500/30
                                       text-green-400 rounded flex items-center gap-1"
                            >
                              <Save className="w-3 h-3" />
                              Save to KB
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Logs */}
        <div className="bg-[#161b22]/30 backdrop-blur-xl border border-[#30363d] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Activity Log</h2>

          <div className="bg-[#0d1117] border border-[#30363d] rounded-lg p-4 h-64 overflow-y-auto font-mono text-xs">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No logs yet</div>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="text-gray-400 py-1">{log}</div>
              ))
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
